import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';

import {
    FaArrowLeft,
    FaWallet,
    FaCoins,
    FaPercentage,
    FaChartLine
} from 'react-icons/fa';

import {
    useNavigate
} from 'react-router-dom';

import {
    useTranslation
} from 'react-i18next';

import '../css/MyEarningsPage.css';


function MyEarningsPage() {

    const navigate = useNavigate();

    const { t } = useTranslation();


    const [earnings, setEarnings] =
        useState(null);

    const [payouts, setPayouts] =
        useState([]);


    const [loading, setLoading] =
        useState(true);

    const [payoutLoading, setPayoutLoading] =
        useState(false);

    const [payoutMessage, setPayoutMessage] =
        useState("");

    const [showPayoutModal, setShowPayoutModal] =
        useState(false);

    const [selectedPayoutMethod, setSelectedPayoutMethod] =
        useState("");

    const [payoutDestination, setPayoutDestination] =
        useState("");


    const [error, setError] =
        useState('');


    const token =
        localStorage.getItem('authToken');


    const authorizationHeader =
        useMemo(() => {

            if (!token) {
                return null;
            }


            return token.startsWith('Bearer ')
                ? token
                : `Bearer ${token}`;

        }, [token]);



    const fetchPayouts =
        useCallback(async () => {

            if (!authorizationHeader) {
                return;
            }

            try {

                const response =
                    await fetch(
                        `${process.env.REACT_APP_API_URL}/api/earnings/payouts?page=0&size=20`,
                        {
                            headers: {
                                Authorization:
                                    authorizationHeader
                            }
                        }
                    );

                const data =
                    await response
                        .json()
                        .catch(() => null);

                if (!response.ok) {

                    throw new Error(
                        data?.message ||
                        "Unable to load payout history."
                    );
                }

                setPayouts(
                    Array.isArray(
                        data?.content
                    )
                        ? data.content
                        : []
                );

            } catch (error) {

                console.error(
                    "Payout history error:",
                    error
                );
            }

        }, [
            authorizationHeader
        ]);

    useEffect(() => {

        fetchPayouts();

    }, [
        fetchPayouts
    ]);

    const openPayoutModal =
        (method) => {

            setSelectedPayoutMethod(
                method
            );

            setPayoutDestination(
                ""
            );

            setShowPayoutModal(
                true
            );
        };





    // =========================================================
    // LOAD EARNINGS
    // =========================================================

    const fetchEarnings =
        useCallback(async () => {

            if (!authorizationHeader) {

                setError(
                    t(
                        'earnings.loginRequired',
                        'Please log in to view your earnings.'
                    )
                );

                setLoading(false);

                return;
            }


            try {

                setLoading(true);

                setError('');


                const response =
                    await fetch(

                        `${process.env.REACT_APP_API_URL}/api/earnings/me`,

                        {
                            headers: {
                                Authorization:
                                    authorizationHeader
                            }
                        }
                    );


                const data =
                    await response
                        .json()
                        .catch(() => null);


                if (!response.ok) {

                    throw new Error(
                        data?.message ||
                        'Unable to load earnings.'
                    );
                }


                setEarnings(data);


            } catch (error) {

                console.error(
                    '❌ Earnings loading error:',
                    error
                );


                setError(
                    error?.message ||
                    t(
                        'earnings.loadError',
                        'Unable to load your earnings.'
                    )
                );


            } finally {

                setLoading(false);
            }

        }, [
            authorizationHeader,
            t
        ]);



    useEffect(() => {

        fetchEarnings();

    }, [fetchEarnings]);



    // =========================================================
    // FORMAT
    // =========================================================

    const available =
        Number(
            earnings?.availableEarnings || 0
        );


    const total =
        Number(
            earnings?.totalEarnings || 0
        );


    const share =
        Number(
            earnings?.creatorSharePercent || 90
        );


    const platformShare =
        Number(
            earnings?.platformSharePercent || 10
        );


    const availableUsd =
        Number(
            earnings?.availableUsd || 0
        );


    const totalUsd =
        Number(
            earnings?.totalUsd || 0
        );



    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {

        return (

            <main className="earnings-page">

                <div className="earnings-state">

                    <div className="earnings-spinner" />

                    <p>
                        {t(
                            'common.loading',
                            'Loading...'
                        )}
                    </p>

                </div>

            </main>
        );
    }



    // =========================================================
    // ERROR
    // =========================================================

    if (error) {

        return (

            <main className="earnings-page">

                <header className="earnings-topbar">

                    <button
                        type="button"
                        onClick={() =>
                            navigate(-1)
                        }
                    >
                        <FaArrowLeft />
                    </button>

                    <h1>
                        {t(
                            'earnings.title',
                            'My Earnings'
                        )}
                    </h1>

                </header>


                <div className="earnings-state">

                    <p className="earnings-error">
                        {error}
                    </p>


                    <button
                        type="button"
                        className="earnings-retry"
                        onClick={fetchEarnings}
                    >
                        {t(
                            'common.retry',
                            'Try again'
                        )}
                    </button>

                </div>

            </main>
        );
    }

    const PayoutStatusBadge = ({
        status
    }) => {

        const normalized =
            String(
                status || ""
            ).toUpperCase();


        const background =
            normalized === "PAID"
                ? "#dcfce7"
                : normalized === "REJECTED"
                    ? "#fee2e2"
                    : "#fef3c7";


        const color =
            normalized === "PAID"
                ? "#15803d"
                : normalized === "REJECTED"
                    ? "#b91c1c"
                    : "#b45309";


        return (

            <span
                style={{
                    display: "inline-block",
                    padding: "6px 11px",
                    borderRadius: "999px",
                    fontSize: "11px",
                    fontWeight: "800",
                    background,
                    color
                }}
            >
                {normalized}
            </span>
        );
    };

    const requestPayout =
        async (
            method,
            destination
        ) => {

            if (!authorizationHeader) {

                setError(
                    "Please log in to request a payout."
                );

                return;
            }


            if (
                !method ||
                !destination ||
                !destination.trim()
            ) {

                setError(
                    "Payout destination is required."
                );

                return;
            }


            try {

                setPayoutLoading(true);

                setPayoutMessage("");

                setError("");


                const response =
                    await fetch(

                        `${process.env.REACT_APP_API_URL}/api/earnings/withdraw?method=${encodeURIComponent(
                            method
                        )}&destination=${encodeURIComponent(
                            destination.trim()
                        )}`,

                        {
                            method: "POST",

                            headers: {
                                Authorization:
                                    authorizationHeader
                            }
                        }
                    );


                const data =
                    await response
                        .json()
                        .catch(() => null);


                if (!response.ok) {

                    throw new Error(
                        data?.message ||
                        "Unable to create payout request."
                    );
                }


                setPayoutMessage(
                    `Payout request submitted successfully: $${Number(
                        data?.amountUsd || 0
                    ).toFixed(2)}`
                );


                // Refresh creator earnings
                // Available earnings should become 0
                // because the payout amount is now reserved.
                await fetchEarnings();

                await fetchPayouts();


                // Close custom modal
                setShowPayoutModal(false);


                // Clear modal fields
                setSelectedPayoutMethod("");

                setPayoutDestination("");


            } catch (error) {

                console.error(
                    "Payout request error:",
                    error
                );


                setError(
                    error?.message ||
                    "Unable to create payout request."
                );


            } finally {

                setPayoutLoading(false);
            }
        };



    // =========================================================
    // PAGE
    // =========================================================

    return (

        <main className="earnings-page">


            {/* HEADER */}

            <header className="earnings-topbar">

                <button
                    type="button"
                    className="earnings-back"
                    onClick={() =>
                        navigate(-1)
                    }
                    aria-label="Back"
                >
                    <FaArrowLeft />
                </button>


                <div>

                    <h1>
                        {t(
                            'earnings.title',
                            'My Earnings'
                        )}
                    </h1>

                    <p>
                        {t(
                            'earnings.subtitle',
                            'Track what you earn from Live gifts.'
                        )}
                    </p>

                </div>

            </header>



            {/* MAIN AVAILABLE BALANCE */}

            <section className="earnings-hero">

                <div className="earnings-hero-icon">
                    <FaWallet />
                </div>


                <span>
                    {t(
                        'earnings.available',
                        'Available earnings'
                    )}
                </span>


                <strong>
                    ${availableUsd.toFixed(2)}
                </strong>

                <small>
                    {available.toFixed(2)} earning coins
                </small>

            </section>



            {/* STATISTICS */}

            <section className="earnings-grid">


                <article className="earnings-card">

                    <div className="earnings-card-icon">

                        <FaChartLine />

                    </div>


                    <span>
                        {t(
                            'earnings.totalEarned',
                            'Total earned'
                        )}
                    </span>


                    <strong>
                        ${totalUsd.toFixed(2)}
                    </strong>

                    <small>
                        {total.toFixed(2)} earning coins
                    </small>

                </article>



                <article className="earnings-card">

                    <div className="earnings-card-icon">

                        <FaPercentage />

                    </div>


                    <span>
                        {t(
                            'earnings.creatorShare',
                            'Creator share'
                        )}
                    </span>


                    <strong>
                        {share}%
                    </strong>

                </article>



                <article className="earnings-card earnings-card-wide">

                    <div className="earnings-card-icon">

                        <FaCoins />

                    </div>


                    <div>

                        <span>
                            {t(
                                'earnings.howItWorks',
                                'How earnings work'
                            )}
                        </span>


                        <p>
                            You receive {share}% of eligible Live gift earnings.
                            HalalHeart keeps {platformShare}% as the platform share.
                        </p>

                    </div>

                </article>

            </section>

            <section className="earnings-recent">

                <div className="earnings-section-title">

                    <div>

                        <h2>
                            Payout History
                        </h2>

                        <p>
                            Track your creator withdrawal requests.
                        </p>

                    </div>

                </div>


                <div
                    style={{
                        background: "#fff",
                        borderRadius: "18px",
                        overflow: "hidden",
                        border: "1px solid #ececf1"
                    }}
                >

                    {payouts.length === 0 ? (

                        <div
                            style={{
                                padding: "30px",
                                textAlign: "center",
                                color: "#71717a"
                            }}
                        >
                            No payout requests yet.
                        </div>

                    ) : (

                        payouts.map(
                            payout => (

                                <div
                                    key={payout.id}
                                    style={{
                                        padding: "18px",
                                        borderBottom: "1px solid #f1f1f4"
                                    }}
                                >

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "1fr 1fr 1fr 1fr",
                                            gap: "15px",
                                            alignItems: "center"
                                        }}
                                    >

                                        <div>
                                            <div
                                                style={{
                                                    fontSize: "11px",
                                                    color: "#a1a1aa",
                                                    marginBottom: "4px"
                                                }}
                                            >
                                                Amount
                                            </div>

                                            <strong>
                                                ${Number(
                                                    payout.amountUsd || 0
                                                ).toFixed(2)}
                                            </strong>
                                        </div>


                                        <div>
                                            <div
                                                style={{
                                                    fontSize: "11px",
                                                    color: "#a1a1aa",
                                                    marginBottom: "4px"
                                                }}
                                            >
                                                Method
                                            </div>

                                            <strong>
                                                {payout.method || "-"}
                                            </strong>
                                        </div>


                                        <div>
                                            <div
                                                style={{
                                                    fontSize: "11px",
                                                    color: "#a1a1aa",
                                                    marginBottom: "4px"
                                                }}
                                            >
                                                Requested
                                            </div>

                                            {
                                                payout.requestedAt
                                                    ? new Date(
                                                        payout.requestedAt
                                                    ).toLocaleString()
                                                    : "-"
                                            }
                                        </div>


                                        <div>

                                            <PayoutStatusBadge
                                                status={payout.status}
                                            />

                                        </div>

                                    </div>


                                    <div
                                        style={{
                                            marginTop: "15px",
                                            paddingTop: "15px",
                                            borderTop: "1px solid #f1f1f4",
                                            display: "grid",
                                            gridTemplateColumns:
                                                "repeat(auto-fit, minmax(180px, 1fr))",
                                            gap: "15px"
                                        }}
                                    >

                                        <div>
                                            <div
                                                style={{
                                                    fontSize: "11px",
                                                    color: "#a1a1aa",
                                                    marginBottom: "4px"
                                                }}
                                            >
                                                Destination
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: "13px",
                                                    fontWeight: "600"
                                                }}
                                            >
                                                {
                                                    payout.payoutDestination ||
                                                    "-"
                                                }
                                            </div>
                                        </div>


                                        {payout.status === "PAID" && (

                                            <>
                                                <div>

                                                    <div
                                                        style={{
                                                            fontSize: "11px",
                                                            color: "#a1a1aa",
                                                            marginBottom: "4px"
                                                        }}
                                                    >
                                                        Payment reference
                                                    </div>

                                                    <div
                                                        style={{
                                                            fontSize: "13px",
                                                            fontWeight: "600"
                                                        }}
                                                    >
                                                        {
                                                            payout.paymentReference ||
                                                            "-"
                                                        }
                                                    </div>

                                                </div>


                                                <div>

                                                    <div
                                                        style={{
                                                            fontSize: "11px",
                                                            color: "#a1a1aa",
                                                            marginBottom: "4px"
                                                        }}
                                                    >
                                                        Paid at
                                                    </div>

                                                    <div
                                                        style={{
                                                            fontSize: "13px",
                                                            fontWeight: "600"
                                                        }}
                                                    >
                                                        {
                                                            payout.paidAt
                                                                ? new Date(
                                                                    payout.paidAt
                                                                ).toLocaleString()
                                                                : "-"
                                                        }
                                                    </div>

                                                </div>
                                            </>

                                        )}

                                    </div>

                                </div>

                            )
                        )

                    )}

                </div>

            </section>



            <section className="earnings-recent">

                <div className="earnings-section-title">

                    <div>

                        <h2>
                            {t(
                                'earnings.withdraw',
                                'Withdraw earnings'
                            )}
                        </h2>

                        <p>
                            {t(
                                'earnings.withdrawSubtitle',
                                'Choose how you would like to receive your creator earnings.'
                            )}
                        </p>

                    </div>

                </div>


                <div className="earnings-payout-box">

                    <div className="earnings-payout-balance">

                        <span>
                            Available to withdraw
                        </span>

                        <strong>
                            ${availableUsd.toFixed(2)}
                        </strong>

                    </div>


                    <div className="earnings-payout-methods">

                        <button
                            type="button"
                            className="earnings-payout-method"
                            disabled={
                                payoutLoading ||
                                availableUsd <= 0
                            }
                            onClick={() =>
                                openPayoutModal(
                                    "PAYPAL"
                                )
                            }
                        >
                            <span>💳</span>

                            <div>
                                <strong>PayPal</strong>

                                <small>
                                    Receive earnings with your PayPal account.
                                </small>
                            </div>
                        </button>

                        <button
                            type="button"
                            className="earnings-payout-method"
                            disabled={
                                payoutLoading ||
                                availableUsd <= 0
                            }
                            onClick={() =>
                                openPayoutModal(
                                    "BANK"
                                )
                            }
                        >
                            <span>🏦</span>

                            <div>
                                <strong>Bank transfer</strong>

                                <small>
                                    Transfer earnings to an eligible bank account.
                                </small>
                            </div>
                        </button>

                        <button
                            type="button"
                            className="earnings-payout-method"
                            disabled={
                                payoutLoading ||
                                availableUsd <= 0
                            }
                            onClick={() =>
                                openPayoutModal(
                                    "WALLET"
                                )
                            }
                        >
                            <span>📱</span>

                            <div>
                                <strong>Wallet</strong>

                                <small>
                                    Supported digital wallet payout.
                                </small>
                            </div>
                        </button>

                    </div>


                    <div className="earnings-payout-note">

                        <strong>
                            How payouts work
                        </strong>

                        <p>
                            Your Live gift earnings accumulate here.
                            You receive 90% of eligible gift earnings,
                            while HalalHeart keeps a 10% platform share.
                            Eligible earnings can be withdrawn through
                            supported payout methods after payout
                            verification is completed.
                        </p>

                    </div>

                </div>

                {payoutMessage && (

                    <div
                        style={{
                            marginBottom: "14px",
                            padding: "12px 14px",
                            borderRadius: "12px",
                            background: "#dcfce7",
                            color: "#166534",
                            fontWeight: "700"
                        }}
                    >
                        {payoutMessage}
                    </div>

                )}

            </section>



            {showPayoutModal && (

                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                        padding: "20px"
                    }}
                >

                    <div
                        style={{
                            width: "100%",
                            maxWidth: "440px",
                            background: "#fff",
                            borderRadius: "20px",
                            padding: "24px",
                            boxShadow:
                                "0 24px 70px rgba(0,0,0,.20)"
                        }}
                    >

                        <h2
                            style={{
                                marginTop: 0
                            }}
                        >
                            Withdraw earnings
                        </h2>

                        <p
                            style={{
                                color: "#71717a"
                            }}
                        >
                            {selectedPayoutMethod === "PAYPAL"
                                ? "Enter your PayPal email."
                                : selectedPayoutMethod === "BANK"
                                    ? "Enter your bank payout information."
                                    : "Enter your wallet destination."
                            }
                        </p>


                        <input
                            type={
                                selectedPayoutMethod === "PAYPAL"
                                    ? "email"
                                    : "text"
                            }
                            value={
                                payoutDestination
                            }
                            onChange={e =>
                                setPayoutDestination(
                                    e.target.value
                                )
                            }
                            placeholder={
                                selectedPayoutMethod === "PAYPAL"
                                    ? "creator@example.com"
                                    : "Payout destination"
                            }
                            style={{
                                width: "100%",
                                padding: "13px 14px",
                                border:
                                    "1px solid #d4d4d8",
                                borderRadius: "12px",
                                outline: "none",
                                boxSizing: "border-box",
                                fontSize: "14px"
                            }}
                        />


                        <div
                            style={{
                                marginTop: "22px",
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "10px"
                            }}
                        >

                            <button
                                type="button"
                                onClick={() =>
                                    setShowPayoutModal(
                                        false
                                    )
                                }
                                style={{
                                    border:
                                        "1px solid #e4e4e7",
                                    background:
                                        "#fff",
                                    padding:
                                        "10px 16px",
                                    borderRadius:
                                        "10px",
                                    cursor:
                                        "pointer",
                                    fontWeight:
                                        "700"
                                }}
                            >
                                Cancel
                            </button>


                            <button
                                type="button"
                                disabled={
                                    payoutLoading ||
                                    !payoutDestination.trim()
                                }
                                onClick={async () => {

                                    await requestPayout(
                                        selectedPayoutMethod,
                                        payoutDestination.trim()
                                    );

                                    setShowPayoutModal(false);
                                }}
                                style={{
                                    border: "none",
                                    background:
                                        payoutLoading ||
                                            !payoutDestination.trim()
                                            ? "#f9a8d4"
                                            : "#ff4f87",
                                    color: "#fff",
                                    padding: "10px 18px",
                                    borderRadius: "10px",
                                    cursor:
                                        payoutLoading ||
                                            !payoutDestination.trim()
                                            ? "not-allowed"
                                            : "pointer",
                                    fontWeight: "800"
                                }}
                            >
                                {
                                    payoutLoading
                                        ? "Submitting..."
                                        : "Request payout"
                                }
                            </button>

                        </div>

                    </div>

                </div>

            )
            }


        </main >
    );
}


export default MyEarningsPage;